import classNames from 'classnames'
import constants from '@/constants'
import currencies from '@/util/currencies'
import { getSportsEventData, USE_DUMMY_DATA, isSportsMarketResolved } from '@/features/market/utils/sportsEvent'
import {
  buildOrderedSportOutcomes,
  resolveSportOutcomeSlots,
  getSportOutcomeLabel,
} from '@/features/market/utils/sportOutcomeSlots'
import { getPositionLabels } from '@/features/market/utils/positionLabels'
import { sideMatchesResolutionDisplay } from '@/features/market/outcome-item/utils/outcomeState'
import { useTruncatedLabelTooltip } from './useTruncatedLabelTooltip'
import Thumbnail from '@/components/ui/thumbnail'
import { getTeamSideImage } from '@/features/market/utils/teamFlag'
import OutcomeLine from './OutcomeLine'
import { OUTCOME_LINES_MIN_HEIGHT, OUTCOME_LINES_WIDTH } from './outcomePointer'

/**
 * The sides of a fixture, drawn as buttons on the card and as lines in the list. A
 * soccer match sells one outcome per side (home / draw / away); the other sports sell
 * the match as one outcome whose long and short labels name the two sides. Both come
 * out as the same buttons, so every fixture card reads alike.
 */
const SportEventOutcomesList = ({
  question,
  currencyId = currencies.OOM.id,
  selectedId,
  /** The position the card holds: on a one-outcome fixture it tells the lit side apart. */
  globalPosition = null,
  onItemClick,
  onSetActiveTab = () => {},
  ui,
  sortedOutcomes = [],
  /** Lines sized for the schedule's fixture rows instead of the card's buttons. */
  compact = false,
}) => {
  /* A side the line cut off is read from a hover hint, since the buttons no longer
     widen to make room for it. */
  const [labelHintProps, labelHint] = useTruncatedLabelTooltip()
  const sportsData = getSportsEventData(question) || question?.event_extra_data || {}
  const rawOutcomes = USE_DUMMY_DATA ? (sportsData?.outcomes ?? []) : (question?.outcomes ?? [])
  const isResolved = isSportsMarketResolved(question)
  const marketTypeId = currencyId === currencies.OOM.id ? currencies.OOM.id : question?.canonical_currency
  const clickable = Boolean(onItemClick)

  /* Built on every render: the handlers the card passes are new each time, so a memo
     would never hold, and the work is a handful of comparisons. */
  const sides = (() => {
    const toPercentage = price => Math.round(100 * (price || 0))

    const select = (outcome, position) => {
      if (!onItemClick) return
      /* Every side opens the pad, whatever the market's state: a match whose odds come
         from the feed before it takes bets, a paused one, a resolved one. The pad itself
         says why no bet can be placed. */
      onSetActiveTab('buy')
      const outcomeToSend =
        ui?.placeBet?.outcomeSource === 'first' ? (sortedOutcomes[0] ?? question.outcomes?.[0] ?? outcome) : outcome
      onItemClick(outcomeToSend, position)
    }

    // One outcome: its two positions are the sides.
    if (rawOutcomes.length === 1) {
      const outcome = rawOutcomes[0]
      /* The labels name the sides; without them the helper would answer Yes / No, so the
         teams themselves stand in. */
      const hasLabels = Boolean(outcome.long_label && outcome.short_label)
      const labels = hasLabels
        ? getPositionLabels(outcome, question)
        : {
            longLabel: sportsData.home_team_short_code ?? sportsData.home_team,
            shortLabel: sportsData.away_team_short_code ?? sportsData.away_team,
          }
      const longPrice = outcome.price?.[marketTypeId] ?? 0
      return [
        { key: 'long', label: labels.longLabel, price: longPrice, position: constants.position.LONG },
        { key: 'short', label: labels.shortLabel, price: 1 - longPrice, position: constants.position.SHORT },
      ].map(side => ({
        key: side.key,
        team: side.key === 'long' ? 'home' : 'away',
        label: side.label,
        percentage: toPercentage(side.price),
        won: isResolved && sideMatchesResolutionDisplay(outcome, question, side.position),
        /* The card names the lit side as `<id>_long` / `<id>_short` (a mono market's
           virtual outcomes) or as the outcome id plus the position it holds. */
        selected:
          selectedId != null &&
          (selectedId === `${outcome.id}_${side.key}` ||
            (selectedId === outcome.id && globalPosition === side.position)),
        select: () => select(outcome, side.position),
      }))
    }

    // One outcome per side, read as home / draw / away.
    const slots = resolveSportOutcomeSlots(rawOutcomes, sportsData)
    return buildOrderedSportOutcomes(slots, rawOutcomes).map(outcome => {
      const betOutcome =
        sortedOutcomes.find(o => o.id === outcome.id) ?? question.outcomes?.find(o => o.id === outcome.id) ?? outcome
      return {
        key: outcome.id ?? outcome.title,
        team: outcome.id === slots.home?.id ? 'home' : outcome.id === slots.away?.id ? 'away' : null,
        tie: outcome.id != null && outcome.id === slots.tie?.id,
        label: getSportOutcomeLabel(outcome, slots, sportsData),
        percentage: toPercentage(outcome.price?.[marketTypeId]),
        won: isResolved && outcome.id == question.resolution,
        selected: selectedId != null && selectedId === betOutcome.id,
        select: () => select(betOutcome, constants.position.LONG),
      }
    })
  })()

  /* A resolved fixture row keeps every side on the line, the winner marked: the result
     reads from the row itself, the way a resolved market row reads. The grid card has one
     cell's worth of space for its odds and keeps showing the winner alone. */
  const winningSides = sides.filter(side => side.won)
  /* A resolution the labels cannot be matched to (a name spelt differently, a void)
     leaves no side marked; the card then keeps every side rather than an empty cell. */
  const sidesToShow = compact || !isResolved || !winningSides.length ? sides : winningSides

  if (!sidesToShow.length) return null

  /* The draw takes a neutral disc with an equals sign where a side has its crest, and a
     side without a crest takes the bare disc, so the labels line up. */
  const renderCrest = side => {
    const crest = side.team ? getTeamSideImage(question, side.team) : null
    if (!crest) {
      return (
        <span
          className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-surface-3 text-xs font-bold leading-none text-Neutral-4"
          aria-hidden>
          {side.tie ? '=' : null}
        </span>
      )
    }
    return <Thumbnail image={crest} alt="" className="h-5 w-5 shrink-0 rounded-full" variant="rect" eager />
  }

  const renderSide = side => {
    /* The row draws its sides as the market row draws its outcomes: a line of text each,
       so the two lists read as one table. */
    if (compact) {
      return (
        <OutcomeLine
          key={side.key}
          label={side.label}
          percentage={side.percentage}
          winner={side.won}
          selected={side.selected}
          clickable={clickable}
          faded={isResolved && !side.won}
          onClick={side.select}
          hintProps={labelHintProps}
          leading={renderCrest(side)}
        />
      )
    }

    const onActivate = event => {
      event.stopPropagation()
      side.select()
    }

    return (
      <div
        key={side.key}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable ? 0 : undefined}
        aria-pressed={clickable ? side.selected : undefined}
        {...labelHintProps}
        onClick={clickable ? onActivate : undefined}
        onKeyDown={
          clickable
            ? event => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  onActivate(event)
                }
              }
            : undefined
        }
        className={classNames(
          'flex h-16 w-full min-w-0 flex-col items-center justify-center gap-1 rounded-lg px-4 leading-[120%]',
          'bg-surface-3 text-Neutral-6',
          {
            'cursor-pointer': clickable,
            'hover:border hover:border-solid hover:border-Neutral-6': clickable,
            'border border-solid border-Neutral-6 font-bold': side.selected,
            'cursor-not-allowed': !clickable,
            /* On a resolved fixture the losers fade and the winner keeps full strength. */
            'opacity-50': !clickable || (isResolved && !side.won),
          }
        )}>
        <div className="w-full min-w-0 truncate text-center font-normal" data-truncatable-label>
          {side.label}
        </div>
        {side.won ? (
          <span className="shrink-0" aria-hidden="true">
            🏆
          </span>
        ) : (
          <div className="shrink-0 font-bold tabular-nums">{side.percentage}%</div>
        )}
      </div>
    )
  }

  return (
    <div
      /* In the row the sides stack in the column every row shares, so the cells before it
         line up across the list, and hold three lines' height so a two-sided fixture's
         row is as tall as a soccer row's; the card lays its own out side by side. */
      className={classNames(
        compact
          ? `flex flex-col justify-center gap-0.5 ${OUTCOME_LINES_WIDTH} ${OUTCOME_LINES_MIN_HEIGHT}`
          : 'flex w-full flex-row items-center justify-center gap-1'
      )}>
      {sidesToShow.map(renderSide)}
      {labelHint}
    </div>
  )
}

export default SportEventOutcomesList
